import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logOut } from '../firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUser, FiLogOut, FiMenu, FiX, 
  FiGrid, FiHome, FiPhone
} from 'react-icons/fi';
import { GiCupcake, GiPhotoCamera } from 'react-icons/gi';
import '../assets/css/styles.css';
import { ScrollContext } from '../contexts/ScrollContext';
import { FaSearch, FaRegUser, FaMapMarkerAlt, FaQuestion, FaChevronDown } from 'react-icons/fa';
import CategoryNavigation from './CategoryNavigation';
import { getAllCategories } from '../services/categoryService';

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrolled } = useContext(ScrollContext);
  const profileRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    setIsMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);
  
  // Fetch categories for the mobile menu
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await getAllCategories(true);
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching categories for navbar:', err);
      }
    };
    
    fetchCategories();
  }, []);

  const handleLogout = async () => {
    try {
      const result = await logOut();
      if (result.success) {
        navigate('/');
      } else {
        console.error(result.message);
      }
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const isActive = (path) => {
    // Exact match for homepage
    if (path === '/' && location.pathname === '/') return true;
    // For other pages, check if the location pathname starts with the given path
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Animation variants
  const navbarVariants = {
    initial: { opacity: 0, y: -20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 }
    }
  };

  const mobileMenuVariants = {
    closed: { 
      opacity: 0,
      height: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    open: { 
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  const userMenuVariants = {
    closed: { 
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: { duration: 0.2, ease: "easeInOut" }
    },
    open: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.2, ease: "easeInOut" }
    }
  };

  const searchVariants = {
    closed: { 
      opacity: 0,
      y: -10,
      transition: { duration: 0.2, ease: "easeInOut" }
    },
    open: { 
      opacity: 1,
      y: 0,
      transition: { duration: 0.2, ease: "easeInOut" }
    }
  };

  return (
    <>
      <motion.nav 
        initial="initial"
        animate="animate"
        variants={navbarVariants}
        className={`sticky top-0 w-full z-50 transition-all duration-300 bg-gradient-to-r from-white to-pink-50 backdrop-blur-sm border-b border-pink-100/30`}
      >
        {/* Top header with logo, search, help and login */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo and mobile menu button */}
            <div className="flex items-center gap-2">
              {/* Mobile menu button - moved to be next to logo */}
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-full text-primary hover:text-primary-dark hover:bg-pink-50 focus:outline-none transition-colors duration-300"
                  aria-expanded="false"
                >
                  <span className="sr-only">Open main menu</span>
                  {isMenuOpen ? (
                    <FiX className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <FiMenu className="block h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </div>

              {/* Logo */}
              <motion.div 
                className="flex-shrink-0 flex items-center"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <Link to="/" className="flex items-center gap-3">
                  <div className="h-20 w-auto flex items-center justify-center">
                    <img src="/icon.png" alt="Habba Events Logo" className="h-20 w-auto object-contain" style={{ maxWidth: '200px', minWidth: '150px' }} />
                  </div>
                </Link>
              </motion.div>
            </div>

            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex items-center gap-6 text-gray-700">
              <Link 
                to="/" 
                className={`font-medium text-sm hover:text-pink-600 transition-colors duration-200 ${
                  isActive('/') ? 'text-pink-600' : ''
                }`}
              >
                HOME
              </Link>
              <Link 
                to="/categories" 
                className={`font-medium text-sm hover:text-pink-600 transition-colors duration-200 ${
                  isActive('/categories') ? 'text-pink-600' : ''
                }`}
              >
                ALL CATEGORIES
              </Link>
              <Link 
                to="/gallery" 
                className={`font-medium text-sm hover:text-pink-600 transition-colors duration-200 ${
                  isActive('/gallery') ? 'text-pink-600' : ''
                }`}
              >
                GALLERY
              </Link>
              <Link 
                to="/about" 
                className={`font-medium text-sm hover:text-pink-600 transition-colors duration-200 ${
                  isActive('/about') ? 'text-pink-600' : ''
                }`}
              >
                ABOUT
              </Link>
              <Link 
                to="/contact" 
                className={`font-medium text-sm hover:text-pink-600 transition-colors duration-200 ${
                  isActive('/contact') ? 'text-pink-600' : ''
                }`}
              >
                CONTACT
              </Link>
            </div>

            {/* Right navigation items - desktop */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-center px-3 py-1.5 bg-white/70 rounded-full border border-pink-200 shadow-sm">
                <FaMapMarkerAlt className="text-pink-600 mr-1.5" />
                <span className="font-medium text-sm text-pink-800">BANGALORE</span>
              </div>
              
              {currentUser ? (
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-pink-200 hover:bg-pink-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:ring-opacity-50 shadow-sm"
                  >
                    <div className="h-7 w-7 rounded-full bg-gradient-to-br from-pink-400 via-pink-500 to-purple-500 flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                    <span className="font-medium text-sm text-gray-700">ACCOUNT</span>
                  </button>
                  
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial="closed"
                        animate="open"
                        exit="closed"
                        variants={userMenuVariants}
                        className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-xl border border-gray-100 z-50"
                      >
                        <div className="py-2">
                          <div className="px-4 py-3 border-b border-gray-100">
                            <p className="text-sm font-medium text-gray-900">
                              {currentUser.displayName || currentUser.email.split('@')[0]}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                          </div>

                          <Link 
                            to="/dashboard" 
                            className="block px-5 py-2.5 text-sm transition-colors duration-200 flex items-center gap-3 hover:bg-gray-50"
                            style={{ color: 'var(--text-secondary)' }}
                          >
                            <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
                              <FiGrid size={15} />
                            </div>
                            <span>My Dashboard</span>
                          </Link>
                          
                          {isAdmin && (
                            <Link 
                              to="/admin" 
                              className="block px-5 py-2.5 text-sm transition-colors duration-200 flex items-center gap-3 hover:bg-gray-50"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              <div className="h-8 w-8 rounded-lg bg-secondary/10 flex items-center justify-center text-secondary">
                                <GiCupcake size={15} />
                              </div>
                              <span>Admin Dashboard</span>
                            </Link>
                          )}
                        </div>
                        
                        <div className="border-t border-gray-100 mt-1 pt-1">
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-5 py-2.5 text-sm transition-colors duration-200 flex items-center gap-3 hover:bg-gray-50 text-primary-dark hover:text-primary"
                          >
                            <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
                              <FiLogOut size={15} />
                            </div>
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link to="/login" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-pink-200 hover:bg-pink-50 transition-all duration-200 shadow-sm">
                  <FaRegUser className="text-pink-600" />
                  <span className="font-medium text-sm text-gray-700">LOG IN</span>
                </Link>
              )}
            </div>
            
            {/* Mobile right actions */}
            <div className="flex md:hidden items-center">
              {currentUser ? (
                <Link to="/dashboard" className="flex items-center justify-center">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-pink-400 via-pink-500 to-purple-500 flex items-center justify-center shadow-md">
                    <span className="text-white text-sm font-bold">
                      {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </div>
                </Link>
              ) : (
                <Link to="/login" className="flex items-center justify-center px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-400 to-pink-500 text-white text-xs font-medium shadow-md whitespace-nowrap">
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </motion.nav>
        
      {/* Category navigation - always visible on desktop */}
      <div className="hidden md:block w-full bg-gradient-to-r from-white to-pink-50/70 z-40 border-b border-pink-100/30 shadow-sm">
        <CategoryNavigation />
      </div>
      
      {/* Mobile menu, show/hide based on menu state */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="closed"
            animate="open"
            exit="closed"
            variants={mobileMenuVariants}
            className="md:hidden overflow-hidden fixed top-20 left-0 right-0 z-40 rounded-b-xl mx-2"
            style={{
              backgroundColor: 'white',
              borderBottom: '1px solid rgba(255, 110, 199, 0.15)',
              boxShadow: '0 15px 30px rgba(249, 168, 212, 0.15)',
              maxHeight: 'calc(100vh - 80px)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Mobile location display */}
            <div className="px-4 pt-4 flex items-center justify-between border-b border-pink-100/30 pb-4">
              <div className="flex items-center px-3 py-1.5 bg-white/70 rounded-full border border-pink-200 shadow-sm">
                <FaMapMarkerAlt className="text-pink-600 mr-1.5" />
                <span className="font-medium text-sm text-pink-800">BANGALORE</span>
              </div>
            </div>

            <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-pink-200 scrollbar-track-transparent flex-1">
              <div className="px-4 pt-2 pb-3 space-y-1">
                <div className="mt-4 px-1 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Navigation
                </div>
                <div className="grid grid-cols-1 gap-1">
                  <MobileNavLink to="/" icon={<FiHome />} label="Home" isActive={isActive('/')} />
                  <MobileNavLink to="/categories" icon={<FiGrid />} label="All Categories" isActive={isActive('/categories')} />
                  <MobileNavLink to="/gallery" icon={<FiGrid />} label="Gallery" isActive={isActive('/gallery')} />
                  <MobileNavLink to="/about" icon={<FiUser />} label="About" isActive={isActive('/about')} />
                  <MobileNavLink to="/contact" icon={<FiPhone />} label="Contact" isActive={isActive('/contact')} />
                  
                  {/* {isAdmin && (
                    <MobileNavLink to="/admin" icon={<GiCupcake />} label="Admin" isActive={isActive('/admin')} isAdmin={true} />
                  )} */}
                </div>
                
                {/* Categories Section */}
                <div className="mt-4 px-1 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Categories
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {categories.length > 0 ? (
                    categories
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map((category) => (
                        <div key={category.id} className="relative">
                          {category.subcategories && category.subcategories.length > 0 ? (
                            // For categories with subcategories, make it expandable
                            <>
                              <button
                                onClick={() => {
                                  // Toggle this category's dropdown
                                  setActiveCategory(activeCategory === category.id ? null : category.id);
                                }}
                                className={`w-full flex justify-between items-center px-3 py-2.5 font-medium rounded-xl ${
                                  activeCategory === category.id 
                                    ? 'bg-gradient-to-br from-primary/10 to-primary/5 text-primary' 
                                    : 'text-gray-700 hover:text-primary hover:bg-primary/5'
                                }`}
                              >
                                <span>{category.name}</span>
                                <FaChevronDown className={`ml-1 h-3 w-3 transition-transform ${
                                  activeCategory === category.id ? 'transform rotate-180' : ''
                                }`} />
                              </button>
                              
                              {/* Subcategories dropdown */}
                              {activeCategory === category.id && (
                                <div className="pl-5 mt-1 space-y-1">
                                  <Link
                                    to={`/category/${category.slug}`}
                                    className="block px-3 py-2 text-sm font-medium text-primary rounded-xl bg-primary/5"
                                    onClick={() => {
                                      setActiveCategory(null);
                                      setIsMenuOpen(false);
                                    }}
                                  >
                                    All {category.name}
                                  </Link>
                                  
                                  {category.subcategories.map((subcategory) => {
                                    const name = typeof subcategory === 'string' ? subcategory : subcategory.name;
                                    const slug = subcategory.slug || (typeof subcategory === 'string' 
                                      ? subcategory.toLowerCase().replace(/\s+/g, '-') 
                                      : subcategory.name.toLowerCase().replace(/\s+/g, '-'));
                                    
                                    return (
                                      <Link
                                        key={subcategory.id || name}
                                        to={`/category/${category.slug}/${slug}`}
                                        className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 rounded-xl"
                                        onClick={() => {
                                          setActiveCategory(null);
                                          setIsMenuOpen(false);
                                        }}
                                      >
                                        {name}
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          ) : (
                            // For categories without subcategories, direct link
                            <Link
                              to={`/category/${category.slug}`}
                              className="flex items-center px-3 py-2.5 font-medium rounded-xl transition duration-200 text-gray-700 hover:text-primary hover:bg-primary/5"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <span>{category.name}</span>
                            </Link>
                          )}
                        </div>
                      ))
                  ) : (
                    <div className="text-sm text-gray-500 px-3 py-2">No categories available</div>
                  )}
                </div>
              </div>
            </div>
            
            {!currentUser && (
              <div className="p-4 border-t border-gray-100">
                <div className="flex space-x-3">
                  <Link
                    to="/login"
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-center transition-colors duration-200 border-2"
                    style={{ 
                      borderColor: 'var(--primary)',
                      color: 'var(--primary-dark)'
                    }}
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-center transition-colors duration-200 btn-primary"
                  >
                    Sign Up
                  </Link>
                </div>
              </div>
            )}
            
            {currentUser && (
              <div className="p-4 border-t border-gray-100">
                <div className="flex items-center mb-3">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full overflow-hidden relative flex items-center justify-center shadow-inner">
                      <div className="absolute inset-0 bg-gradient-to-br from-pink-400 via-pink-500 to-purple-500"></div>
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_60%)] opacity-30"></div>
                      <span className="relative z-10 text-white font-semibold">
                        {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : 'U'}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">
                      {currentUser.displayName || currentUser.email.split('@')[0]}
                    </div>
                    <div className="text-sm font-medium text-gray-500">{currentUser.email}</div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-100 hover:text-white bg-primary hover:bg-primary-dark rounded-xl transition-colors duration-200"
                >
                  <FiLogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MobileNavLink({ to, icon, label, isActive, isAdmin }) {
  return (
    <Link
      to={to}
      className={`flex items-center px-3 py-2.5 font-medium rounded-xl transition duration-200 ${
        isActive 
          ? 'bg-gradient-to-br from-primary/10 to-primary/5 text-primary' 
          : 'text-gray-700 hover:text-primary hover:bg-primary/5'
      } ${isAdmin ? 'bg-amber-50 text-amber-800' : ''}`}
    >
      <div className={`mr-3 h-8 w-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/80 text-primary' : 'bg-gray-100/80 text-gray-500'}`}>
        {React.cloneElement(icon, { size: 18 })}
      </div>
      <span>{label}</span>
      {isActive && (
        <motion.span 
          className="ml-auto mr-1 w-1.5 h-1.5 rounded-full bg-primary"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      )}
    </Link>
  );
}